const formatCrossings = ({
  crossings = [],
  config,
  requestedResourceCodes,
  productCode,
  searchedProductCode,
  hasValidPromoCode,
}) => {
  const searchedResourceCodes = requestedResourceCodes?.split('|') || []

  const isWalkOn =
    searchedResourceCodes[0] &&
    searchedResourceCodes[0] ===
      config?.ferry?.options?.walkOnPassengerResourceCode

  const passengerResourceCode = isWalkOn
    ? config?.ferry?.options?.walkOnPassengerResourceCode
    : config?.ferry?.options?.passengerResourceCode

  const addOnResourceCodes = config?.ferry?.options?.addOnResourceCodes || []
  const ships = config?.ferry?.options?.ships || []

  const ticketGroupData = config?.ferry?.options?.ticketGroups || []

  const bestRates = {}

  const routesData = Object.values(config?.ferry?.options?.routes || {}).flat()

  const results = crossings.map(crossing => {
    const matchingRouteData =
      routesData?.find(route => route.code === crossing.routeCode) || {}
    const tickets = []
    const addOns = []

    // we need to attach the passengerTicketType .ticketType & .resourceCode
    // to the rates so we can pass them back when creating a cart.
    const passengerTicketType = crossing?.ticketGroups
      ?.find(ticketGroup =>
        ticketGroup.resources?.some(
          resource => resource.resourceCode === passengerResourceCode,
        ),
      )
      ?.resources?.find(
        resource => resource.resourceCode === passengerResourceCode,
      )

    crossing?.ticketGroups?.forEach(ticketGroup => {
      const isAddOn = addOnResourceCodes?.some(
        addOnResource => addOnResource.ticketGroup === ticketGroup.code,
      )
      // if this is not a walk on and this ticket group only includes the passenger type we need to skip it.
      //  it's possible there is another nested resource here that we do need.
      if (
        !isWalkOn &&
        ticketGroup?.resources?.every(
          resource => resource.resourceCode === passengerResourceCode,
        )
      ) {
        return
      }
      if (!isAddOn) {
        const matchingConfigData = ticketGroupData?.find(
          ({ name }) => name === ticketGroup.code,
        )
        // bestRate for this crossing
        let bestRate
        const prices = []
        ticketGroup?.resources?.forEach(resource => {
          if (resource.resourceCode === passengerResourceCode && !isWalkOn) {
            return
          }
          if (resource.totalPrice) {
            prices.push(resource.totalPrice)
          }
          if (!bestRate || +resource.totalPrice < +bestRate.totalPrice) {
            bestRate = {
              ...resource,
              passengerTicketType: passengerTicketType?.ticketType,
              passengerResourceCode: passengerTicketType?.resourceCode,
            }
          }
        })

        const lowestRate = prices?.length ? Math.min(...prices) : null

        // best rate for all crossings
        if (
          lowestRate &&
          (!bestRates[ticketGroup.code] ||
            +lowestRate < bestRates[ticketGroup.code])
        ) {
          bestRates[ticketGroup.code] = +lowestRate
        }

        if (matchingConfigData) {
          tickets.push({
            ...ticketGroup,
            ...matchingConfigData,
            bestRate,
            resources: ticketGroup?.resources?.map(resource => ({
              ...resource,
              passengerTicketType: passengerTicketType?.ticketType,
              passengerResourceCode: passengerTicketType?.resourceCode,
            })),
            passengerTicketType: passengerTicketType?.ticketType,
            passengerResourceCode: passengerTicketType?.resourceCode,
          })
        }
      } else {
        ticketGroup?.resources?.forEach(resource => {
          const matchingData = addOnResourceCodes?.find(
            addOnResourceCode =>
              addOnResourceCode.code === resource.resourceCode,
          )
          addOns.push({
            ...resource,
            description: matchingData?.description,
            usageType: matchingData?.usageType,
          })
        })
      }
    })

    return {
      ...crossing,
      productCode,
      searchedProductCode,
      hasValidPromoCode,
      id: `${crossing.departureDateTime.replace(/ /g, '-')}-${crossing.ship}`,
      shipCode: crossing.ship,
      ship: ships?.find(({ value }) => value === crossing.ship),
      pricing: tickets,
      addOns,
      description: matchingRouteData?.description || '',
      ferryId: matchingRouteData?.ferryId || '',
    }
  })

  return {
    bestRates,
    crossings: results,
  }
}

const reduceCrossingAvailability = (
  res,
  configuration,
  params,
  couponResourceCode,
) => {
  const data = res?.data?.['hydra:member']?.[0]
  if (!data) {
    throw new Error('no data returned')
  }

  const { outwardDepartures, returnDepartures } = data
  const hasValidPromoCode =
    couponResourceCode && couponResourceCode === data.productCode
  //   we need both the productCode and the searchedProduct code because they can be different.
  //  the searchedProductCode is used for keying pricing and the productCode is used when creating a cart.
  const formattedOutward = formatCrossings({
    crossings: outwardDepartures,
    config: configuration,
    requestedResourceCodes: params.departureResourceCodes,
    productCode: data.productCode,
    searchedProductCode: params.productCode,
    hasValidPromoCode,
  })
  const formattedReturn = formatCrossings({
    crossings: returnDepartures,
    config: configuration,
    requestedResourceCodes: params.returnResourceCodes,
    productCode: data.productCode,
    searchedProductCode: params.productCode,
    hasValidPromoCode,
  })

  return {
    ...data,
    hasValidPromoCode,
    bestOutwardRates: formattedOutward?.bestRates || {},
    bestReturnRates: formattedReturn?.bestRates || {},
    outwardDepartures: formattedOutward?.crossings,
    returnDepartures: formattedReturn?.crossings,
  }
}

export default reduceCrossingAvailability
